import { getDay } from 'date-fns';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { RecurringRuleFields } from 'src/shared/Appointments/RecurringRuleFields';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';

import { EditEventFormValues } from './_types';

interface RecurringSettingsSectionProps {
  isRecurring?: boolean;
}

const SESSION_FIELD_NAMES = {
  recurringCount: 'recurringCount',
  recurringType: 'recurringType',
  recurringDayNumbers: 'recurringDayNumbers',
  days: 'recurringDays',
} as const;

export const RecurringSettingsSection: FC<RecurringSettingsSectionProps> = ({
  isRecurring = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.editEventDialog.recurringSettings',
  });
  const { values, setFieldValue } = useFormikContext<EditEventFormValues>();

  // The weekday of the session instance the user opened cannot be deselected
  // (legacy v2 currentScheduledDays). Other days in a multi-day recurrence
  // stay toggleable.
  const scheduledDay = values.startTime ? getDay(values.startTime) : undefined;

  return (
    <Stack spacing={2}>
      {!isRecurring && (
        <Checkbox
          useSwitch
          label={t('makeRecurring')}
          checked={values.isRecurring}
          onChange={(e) => setFieldValue('isRecurring', e.target.checked)}
        />
      )}

      {values.isRecurring && (
        <Box
          component="fieldset"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            m: 0,
          }}
        >
          <Typography variant="h6" component="legend">
            {t('title')}
          </Typography>

          {isRecurring && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('isRecurringNote')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                {t('onlyThisSessionNote')}
              </Typography>
            </>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            <RecurringRuleFields
              fieldNames={SESSION_FIELD_NAMES}
              defaultDay={getDay(values.startTime)}
              lockedDay={scheduledDay}
            />

            <FormDatePicker
              name="endAt"
              label={t('endDate')}
              minDate={values.startTime}
            />

            <Box>
              <Checkbox
                label={t('allowBookAllRecurring')}
                checked={values.bookRecurring}
                onChange={(e) =>
                  setFieldValue('bookRecurring', e.target.checked)
                }
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
